import { action, observable, reaction, makeObservable } from 'mobx';
import {
  contentLookupService,
  SuccessResponse,
  TimeZone,
  timeZoneLoaders,
  userLoaders,
  VersionedContentLookup
} from '../../../domain';
import {Disposers, HttpPromiseState, PromiseState, toastrHelper} from '../../../lib/helpers';
import {routes} from '../../../pages/Routes';
import {UserEditFormStore} from '../edit-form/UserEditFormStore';

export class UserAddSceneStore {
  @observable.ref
  lookupState: PromiseState<VersionedContentLookup> | null;

  @observable.ref
  timeZonesState: HttpPromiseState<readonly TimeZone[]> | null;

  @observable
  redirectTo: string | null;

  @observable.ref
  form: UserEditFormStore | null;

  @observable.ref
  saveState: HttpPromiseState<SuccessResponse> | null;

  private readonly disposers = new Disposers();

  constructor() {
    makeObservable(this);
    this.lookupState = this.timeZonesState = null;
    this.form = null;
    this.saveState = null;
    this.redirectTo = null;

    this.setup();
  }

  @action
  loadLookup = () => {
    if (this.lookupState?.isPending === true)
      return;
    this.lookupState = contentLookupService.load();
  };

  @action
  loadTimeZones = () => {
    if (this.timeZonesState?.isPending === true)
      return;
    this.timeZonesState = timeZoneLoaders.list();
  };

  @action
  save = () => {
    if (this.saveState?.isPending === true)
      return;
    if (this.form === null)
      return;
    this.form.markAllTouched();
    if (this.form.invalid)
      return;
    this.saveState = userLoaders.create(this.form.model);
  };

  @action
  cancel = () => {
    this.redirectTo = routes.UserList;
  };

  @action
  clearError = () => {
    if (this.saveState?.isRejected === true)
      this.saveState = null;
  };

  private setup() {
    this.disposers.add(reaction(
      () => [this.lookupState?.state, this.timeZonesState?.state],
      this.prepareUserEditFormStore
    ));
    this.disposers.add(reaction(
      () => this.saveState?.state,
      this.onSaveStateChange
    ));
  }

  @action
  private prepareUserEditFormStore = () => {
    if (this.form !== null)
      return;
    if (this.lookupState?.isResolved !== true)
      return;
    if (this.timeZonesState?.isResolved !== true)
      return;
    this.form = new UserEditFormStore('create',
      this.lookupState.value.lookup, this.timeZonesState.value);

  };

  @action
  private onSaveStateChange = () => {
    if (this.saveState?.isResolved !== true)
      return;

    toastrHelper.success(this.saveState.value);
    const id = this.saveState.value.Results['Id'];
    if (id === undefined)
      this.redirectTo = routes.UserList;
    else
      this.redirectTo = routes.userView(id);
  };

  dispose = () => {
    this.disposers.dispose();
  };
}
